import { ApolloLink, Observable } from '@apollo/client';

export interface NetworkCallEntry {
  timestamp: string;
  type: string;
  name: string;
  status: 'success' | 'error' | 'pending';
  durationMs?: number;
  errors?: string[];
  statusCode?: number;
}

const MAX_NETWORK_LOGS = 25;
const networkBuffer: NetworkCallEntry[] = [];
let isFetchInitialized = false;

export const recordNetworkCall = (entry: NetworkCallEntry): void => {
  networkBuffer.push(entry);
  if (networkBuffer.length > MAX_NETWORK_LOGS) {
    networkBuffer.shift();
  }
};

export const qaTelemetryLink = new ApolloLink((operation, forward) => {
  const startTime = Date.now();
  const opName = operation.operationName || 'UnnamedOperation';
  const opType = (operation.query?.definitions?.[0] as { operation?: string } | undefined)?.operation || 'query';

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (result) => {
        const durationMs = Date.now() - startTime;
        const errors = result.errors?.map((e) => e.message) || [];
        recordNetworkCall({
          timestamp: new Date().toISOString(),
          type: `graphql:${opType}`,
          name: opName,
          status: errors.length > 0 ? 'error' : 'success',
          durationMs,
          errors: errors.length > 0 ? errors : undefined,
        });
        observer.next(result);
      },
      error: (networkError) => {
        const durationMs = Date.now() - startTime;
        recordNetworkCall({
          timestamp: new Date().toISOString(),
          type: `graphql:${opType}`,
          name: opName,
          status: 'error',
          durationMs,
          errors: [networkError?.message || 'Network request failed'],
        });
        observer.error(networkError);
      },
      complete: () => {
        observer.complete();
      },
    });

    return () => subscription.unsubscribe();
  });
});

export const initFetchLogger = (): void => {
  if (isFetchInitialized || typeof window === 'undefined') return;
  isFetchInitialized = true;

  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const startTime = Date.now();
    const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || 'unknown';

    // Skip telemetry's own submissions to avoid recursion
    if (url.includes('/api/v1/qa_reports')) {
      return originalFetch.apply(window, args);
    }

    try {
      const response = await originalFetch.apply(window, args);
      if (!response.ok && !url.includes('/graphql')) {
        recordNetworkCall({
          timestamp: new Date().toISOString(),
          type: 'fetch',
          name: url.split('?')[0],
          status: 'error',
          statusCode: response.status,
          durationMs: Date.now() - startTime,
          errors: [`HTTP ${response.status} ${response.statusText}`],
        });
      }
      return response;
    } catch (err: unknown) {
      if (!url.includes('/graphql')) {
        recordNetworkCall({
          timestamp: new Date().toISOString(),
          type: 'fetch',
          name: url.split('?')[0],
          status: 'error',
          durationMs: Date.now() - startTime,
          errors: [err instanceof Error ? err.message : 'Network error'],
        });
      }
      throw err;
    }
  };
};

export const getNetworkLogs = (): NetworkCallEntry[] => {
  return [...networkBuffer];
};

export const clearNetworkLogs = (): void => {
  networkBuffer.length = 0;
};
